import { DATE_FORMAT_LONG, DATE_FORMAT_LONGER } from 'common/api/v1/helpers'
import { format, addHours, addDays, addMonths, addMinutes, addSeconds, addMilliseconds, subDays } from 'date-fns'

export const dateFilterPossibleValues = (s?: string): { value: string; displayName: string }[] => {
    let now = new Date()
    now.setMinutes(0, 0, 0)
    now = subDays(now, 1)

    const options: { value: string; displayName: string }[] = []

    let maxValue = 24
    let stride = 1
    let dateFormat = DATE_FORMAT_LONG
    let dateAdderFn = addHours

    if (s) {
        now = new Date(s.replace('T', ' ').replace('Z', ' '))

        if (s.length <= 6) {
            // 2025XXX
            maxValue = 12
            dateAdderFn = addMonths
        } else if (s.length >= 7 && s.length <= 9) {
            // 2025-02XXX
            maxValue = 31
            dateAdderFn = addDays
        } else if (s.length >= 10 && s.length <= 12) {
            // 2025-02-11XXX
            maxValue = 24
            dateAdderFn = addHours
        } else if (s.length >= 13 && s.length <= 15) {
            // 2025-02-11T12XXX
            maxValue = 60
            stride = 5
            dateAdderFn = addMinutes
        } else if (s.length >= 16 && s.length <= 18) {
            // 2025-02-11T12:00XXX
            maxValue = 60
            stride = 5
            dateAdderFn = addSeconds
        } else if (s.length >= 19 && s.length <= 23) {
            // 2025-02-11T12:00:00XXXXX
            maxValue = 1000
            stride = 100
            dateFormat = DATE_FORMAT_LONGER
            dateAdderFn = addMilliseconds
        }
    }

    for (let i = 0; i < maxValue; i += stride) {
        const date = dateAdderFn(now, i)

        let displayName = format(date, dateFormat)
        if (s?.includes('T')) displayName = displayName.replace(' ', 'T')
        if (s?.includes('Z')) displayName = displayName + 'Z'

        options.push({ value: date.toISOString(), displayName })
    }

    return options
}

export interface DateFilterBounds {
    timezone?: 'UTC' | 'system'
    minDate?: Date
    maxDate?: Date
    minDateMessage?: string
    maxDateMessage?: string
}

/** Parses a committed date in the field's timezone and preserves the audit API's inclusive bounds. */
export function parseDateFilterValue(value: string, bounds: DateFilterBounds) {
    const normalized = value.trim().replace(' ', 'T')
    const match = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2})(?::(\d{2})(\.\d{1,3})?)?)?(Z|[+-]\d{2}:\d{2})?$/.exec(
        normalized
    )
    if (!match) throw new Error('Enter a date as YYYY-MM-DD HH:MM:SS')
    const dateTime = `${match[1]}T${match[2] ?? '00:00'}:${match[3] ?? '00'}${match[4] ?? ''}`
    const zone = match[5] ?? (bounds.timezone === 'UTC' ? 'Z' : '')
    const date = new Date(`${dateTime}${zone}`)
    // Date rolls invalid calendar days into the next month. Reject those rather than changing the requested day.
    const calendar = new Date(`${match[1]}T00:00:00Z`)
    if (
        Number(match[2]?.slice(0, 2) ?? 0) > 23 ||
        Number.isNaN(date.getTime()) ||
        Number.isNaN(calendar.getTime()) ||
        calendar.toISOString().slice(0, 10) !== match[1]
    ) {
        throw new Error('Invalid date')
    }
    const minDate = bounds.minDate ?? new Date('2020-01-01T00:00:00Z')
    const maxDate = bounds.maxDate ?? new Date()
    if (date > new Date()) throw new Error('Future dates cannot be specified')
    if (date < minDate) throw new Error(bounds.minDateMessage ?? 'Date is before the allowed range')
    if (date > maxDate) throw new Error(bounds.maxDateMessage ?? 'Date is after the allowed range')
    return {
        value: date.toISOString(),
        displayName:
            bounds.timezone === 'UTC'
                ? `${date.toISOString().replace('T', ' ').replace('.000Z', 'Z').replace('Z', ' UTC')}`
                : format(date, DATE_FORMAT_LONGER),
    }
}
